/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import { throwError as observableThrowError, Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ConfigService } from '../../../services/config/config.service';

interface HttpOptions {
  params?: {
    [param: string]: string | string[];
  }
}

@Injectable()
export class BaseApi {
  endPoint: string = 'not_set';
  host: string;

  constructor(public http: HttpClient) {
    this.host = ConfigService.getApiHost();
  }

  public setEndPoint(endPoint: string) {
    this.endPoint = endPoint;
  }

  public _unimplemented(name: string): Observable<any> {
    // TODO
    console.log('Attempted to call unimplemented method ' + name + ' in ' + this.constructor.name);
    return this._get(this.endPoint + '/' + name + '/not-implemented', {});
  }

  public findById(id: any): Observable<any> {
    const _routeParams = {
      id: id
    };
    return this._get(this.endPoint + '/:id', _routeParams);
  }

  public findByCode(code: any): Observable<any> {
    const dynamicEndPoint = this.endPoint + '/code/:code';
    const _routeParams = {
      code: code
    };
    return this._get(dynamicEndPoint, _routeParams);
  }

  public deleteById(id: any, confirm = false): Observable<any> {
    const _routeParams = {
      id: id,
      confirm,
    };
    return this._delete(this.endPoint + '/:id?confirm=:confirm', _routeParams);
  }

  public list(limit?: number, offset: number = 0): Observable<any> {
    let dynamicEndPoint = this.endPoint;
    let _routeParams = {};
    if (limit && limit > 0) {
      _routeParams = {
        limit: limit,
        offset: offset
      };
      dynamicEndPoint += '/limit/:limit/offset/:offset';
    }
    return this._get(dynamicEndPoint, _routeParams);
  }

  public patch(id: any, _postBody: any = {}): Observable<any> {
    const _routeParams: any = {
      id: id
    };
    return this._patch(this.endPoint + '/:id', _routeParams, _postBody);
  }

  public replace(_postBody: any): Observable<any> {
    if (!_postBody._id) {
      throw observableThrowError('Cannot replace document because no _id was supplied');
    }
    const _routeParams: any = {
      id: _postBody._id
    };
    return this._put(this.endPoint + '/:id', _routeParams, _postBody);
  }

  public create(_postBody: any): Observable<any> {
    const _routeParams: any = {};
    return this._post(this.endPoint, _routeParams, _postBody);
  }

  public get(_url, _routeParams): Observable<any> {
    return this._get(_url, _routeParams);
  }

  public post(_url, _routeParams, _postBody): Observable<any> {
    return this._post(_url, _routeParams, _postBody);
  }

  // Internal methods

  protected _post(_url, _routeParams, _postBody): Observable<any> {
    const _absoluteURL = this._generateURL(_url, _routeParams);
    const _options = {};
    return this.http.post(_absoluteURL, _postBody, _options);
  }

  protected _put(_url, _routeParams, _postBody): Observable<any> {
    const _absoluteURL = this._generateURL(_url, _routeParams);
    const _options = {};
    return this.http.put(_absoluteURL, _postBody, _options);
  }

  protected _patch(_url, _routeParams, _postBody): Observable<any> {
    const _absoluteURL = this._generateURL(_url, _routeParams);
    const _options = {};
    return this.http.patch(_absoluteURL, _postBody, _options);
  }

  protected _get(_url, _routeParams, _options: HttpOptions = {}): Observable<any> {
    const _absoluteURL = this._generateURL(_url, _routeParams);
    return this.http.get(_absoluteURL, _options);
}

  protected _delete(_url, _routeParams): Observable<any> {
    const _absoluteURL = this._generateURL(_url, _routeParams);
    const _options = {};
    return this.http.delete(_absoluteURL, _options);
  }

  public _generateURL(url: string, _routeParams?: Object) {
    let dynamicURL = this.host + url;
    if (!_routeParams) {
      return dynamicURL;
    }

    for (const key of Object.keys(_routeParams)) {
      dynamicURL = dynamicURL.replace(':' + key, encodeURIComponent(_routeParams[key]));
    }
    return dynamicURL;
  }
}
